/**
 * Storage health (W1-13 persist-workers; contract persist/types.ts `StorageHealth`; DESIGN.md §5.13).
 *
 * Worker side: storage facts (usage and quota from `navigator.storage.estimate()`, persistence from
 * `persisted()`, OPFS availability, the last write error). Client side: the save state is computed from
 * acknowledgements, so a dead worker can never look healthy: red when the oldest unacknowledged txn is older than
 * 2 s, while the worker is being respawned, or after a write error.
 */

import type { StorageHealth } from './types';

/** Oldest-unacknowledged age above which the save chip turns red. */
export const UNACKED_RED_MS = 2000;
/** Free space below which the Library and the editor warn (DESIGN.md §5.13). */
export const LOW_FREE_SPACE_BYTES = 200 * 1024 * 1024;

export interface StorageManagerLike {
  estimate?(): Promise<{ readonly usage?: number; readonly quota?: number }>;
  persisted?(): Promise<boolean>;
  /** Window only (the Storage Standard does not expose persist() to workers). */
  persist?(): Promise<boolean>;
}

/** `navigator.storage` when present. */
export function platformStorage(): StorageManagerLike | null {
  const nav = globalThis.navigator as { storage?: StorageManagerLike } | undefined;
  return nav?.storage ?? null;
}

export interface StorageFacts {
  readonly usageBytes: number;
  readonly quotaBytes: number;
  readonly persisted: boolean;
  readonly opfs: boolean;
  readonly lastWriteError: string | null;
}

export const EMPTY_FACTS: StorageFacts = {
  usageBytes: 0,
  quotaBytes: 0,
  persisted: false,
  opfs: false,
  lastWriteError: null,
};

/** Usage, quota and persistence (0 / false when the platform cannot tell). */
export async function readStorageEstimate(
  storage: StorageManagerLike | null,
): Promise<{ usageBytes: number; quotaBytes: number; persisted: boolean }> {
  const estimate = await Promise.resolve(storage?.estimate?.()).catch(() => undefined);
  const persisted = await Promise.resolve(storage?.persisted?.()).catch(() => false);
  return { usageBytes: estimate?.usage ?? 0, quotaBytes: estimate?.quota ?? 0, persisted: persisted ?? false };
}

/** Asks for persistent storage where the API exists in this context (main thread). */
export async function requestPersist(storage: StorageManagerLike | null): Promise<boolean | null> {
  if (storage?.persist === undefined) return null;
  try {
    return await storage.persist();
  } catch {
    return false;
  }
}

/** The worker's view of health (client-only fields neutral). */
export function workerHealth(facts: StorageFacts, lastFlushAt: number): StorageHealth {
  return {
    ...facts,
    lastFlushAt,
    pendingTxns: 0,
    oldestUnackedMs: 0,
    workerAlive: true,
  };
}

export type SaveState = 'saved' | 'saving' | 'error';

/** Red ('error'): write error, dead/respawning worker, or a change unacknowledged for more than 2 s. */
export function saveState(h: StorageHealth): SaveState {
  if (h.lastWriteError !== null || !h.workerAlive || h.oldestUnackedMs > UNACKED_RED_MS) return 'error';
  return h.pendingTxns > 0 ? 'saving' : 'saved';
}

/** Low free space or non-persistent storage: the Library shows the backup banner. */
export function storageAtRisk(h: StorageHealth): boolean {
  const lowSpace = h.quotaBytes > 0 && h.quotaBytes - h.usageBytes < LOW_FREE_SPACE_BYTES;
  return lowSpace || !h.persisted || h.lastWriteError !== null;
}

export function healthEquals(a: StorageHealth, b: StorageHealth): boolean {
  return (
    a.usageBytes === b.usageBytes &&
    a.quotaBytes === b.quotaBytes &&
    a.persisted === b.persisted &&
    a.opfs === b.opfs &&
    a.lastWriteError === b.lastWriteError &&
    a.lastFlushAt === b.lastFlushAt &&
    a.pendingTxns === b.pendingTxns &&
    a.oldestUnackedMs === b.oldestUnackedMs &&
    a.workerAlive === b.workerAlive
  );
}
